import type { Challenge, Milestone, Reward, StreakState } from '../../types/membership';
import { readJSON, writeJSON, delay } from './storage';
import { tokenService } from './tokenService';

const STREAK_KEY = 'teskedo_streak_v1';
const CLAIMED_KEY = 'teskedo_rewards_claimed_v1';
const METRICS_KEY = 'teskedo_metrics_v1';
const CHALLENGE_KEY = 'teskedo_challenges_v1';

const DAILY_TOKENS = 5;
const REWARD_EXPIRY_DAYS = 90;

// Streak milestones → bonus tokens awarded when the streak hits that day count.
const STREAK_BONUSES: Record<number, { tokens: number; note: string }> = {
  7: { tokens: 100, note: '7-day streak bonus' },
  30: { tokens: 500, note: '30-day streak bonus (+50 permanent monthly tokens)' },
  90: { tokens: 2000, note: '90-day streak bonus (badge unlocked)' },
};

function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

function isYesterday(last: Date, now: Date): boolean {
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  return sameDay(last, y);
}

const emptyStreak: StreakState = { current: 0, longest: 0, lastClaim: null };

// One-off claimable rewards (community / engagement).
const baseRewards: Omit<Reward, 'isClaimed'>[] = [
  { id: 'profile_complete', name: 'Complete your profile', tokenValue: 25, criteria: 'Fill out every profile section' },
  { id: 'first_application', name: 'Submit your first application', tokenValue: 30, criteria: 'Apply to any job' },
  { id: 'share_template', name: 'Share a resume template', tokenValue: 40, criteria: 'Community contribution (admin-approved)' },
  { id: 'answer_questions', name: 'Answer 5 community questions', tokenValue: 50, criteria: 'Community contribution (admin-approved)' },
  { id: 'mentor_session', name: 'Mentor someone', tokenValue: 75, criteria: 'Complete a mentoring session (admin-approved)' },
];

const baseMilestones: Omit<Milestone, 'progress' | 'claimed'>[] = [
  { id: 'first_interview', name: 'Land an interview', metric: 'interviewsLanded', requiredValue: 1, tokenReward: 200, extraPerk: 'Interview prep unlock' },
  { id: 'first_client', name: 'Acquire a client', metric: 'clientsAcquired', requiredValue: 1, tokenReward: 300, extraPerk: 'Agency tools preview' },
  { id: 'income_500', name: 'Replace $500 of income', metric: 'incomeReplaced', requiredValue: 500, tokenReward: 500, extraPerk: 'Career Transformer trial' },
];

const baseChallenges: Omit<Challenge, 'progress' | 'joined'>[] = [
  { id: 'sprint_30', name: '30-Day Career Sprint', description: 'Take one career action every day for 30 days.', durationDays: 30, tokenReward: 750, goal: 30 },
  { id: 'apply_10', name: 'Apply to 10 Jobs', description: 'Submit 10 applications this month.', durationDays: 30, tokenReward: 250, goal: 10 },
];

type Metrics = Record<string, number>;

export const rewardsService = {
  // --- Daily streak --------------------------------------------------------
  getStreak(): StreakState {
    return readJSON<StreakState>(STREAK_KEY, emptyStreak);
  },

  canClaimDaily(): boolean {
    const { lastClaim } = this.getStreak();
    if (!lastClaim) return true;
    return !sameDay(new Date(lastClaim), new Date());
  },

  async claimDailyReward(): Promise<{ streak: StreakState; tokensAwarded: number }> {
    const streak = this.getStreak();
    const now = new Date();

    if (streak.lastClaim && sameDay(new Date(streak.lastClaim), now)) {
      return delay({ streak, tokensAwarded: 0 });
    }

    const continues = streak.lastClaim ? isYesterday(new Date(streak.lastClaim), now) : false;
    const current = continues ? streak.current + 1 : 1;
    const next: StreakState = {
      current,
      longest: Math.max(streak.longest, current),
      lastClaim: now.toISOString(),
    };
    writeJSON(STREAK_KEY, next);

    let awarded = DAILY_TOKENS;
    await tokenService.earn(DAILY_TOKENS, 'daily_reward', 'Daily login reward', REWARD_EXPIRY_DAYS);

    const bonus = STREAK_BONUSES[current];
    if (bonus) {
      awarded += bonus.tokens;
      await tokenService.earn(bonus.tokens, 'streak_bonus', bonus.note, REWARD_EXPIRY_DAYS);
    }

    return delay({ streak: next, tokensAwarded: awarded });
  },

  // --- Claimable rewards ---------------------------------------------------
  getRewards(): Reward[] {
    const claimed = readJSON<string[]>(CLAIMED_KEY, []);
    return baseRewards.map((r) => ({ ...r, isClaimed: claimed.includes(r.id) }));
  },

  async claimReward(id: string): Promise<Reward[]> {
    const claimed = readJSON<string[]>(CLAIMED_KEY, []);
    const reward = baseRewards.find((r) => r.id === id);
    if (reward && !claimed.includes(id)) {
      writeJSON(CLAIMED_KEY, [...claimed, id]);
      await tokenService.earn(reward.tokenValue, 'milestone', `Reward: ${reward.name}`, REWARD_EXPIRY_DAYS);
    }
    return delay(this.getRewards());
  },

  // --- Milestones ----------------------------------------------------------
  getMetrics(): Metrics {
    return readJSON<Metrics>(METRICS_KEY, {});
  },

  /** Increment a tracked metric (e.g. interviewsLanded). Returns new metrics. */
  recordMetric(metric: string, amount = 1): Metrics {
    const metrics = this.getMetrics();
    metrics[metric] = (metrics[metric] ?? 0) + amount;
    writeJSON(METRICS_KEY, metrics);
    return metrics;
  },

  getMilestones(): Milestone[] {
    const metrics = this.getMetrics();
    const claimed = readJSON<string[]>(`${CLAIMED_KEY}_milestones`, []);
    return baseMilestones.map((m) => ({
      ...m,
      progress: Math.min(metrics[m.metric] ?? 0, m.requiredValue),
      claimed: claimed.includes(m.id),
    }));
  },

  async claimMilestone(id: string): Promise<Milestone[]> {
    const key = `${CLAIMED_KEY}_milestones`;
    const claimed = readJSON<string[]>(key, []);
    const milestone = baseMilestones.find((m) => m.id === id);
    const metrics = this.getMetrics();
    const reached = milestone ? (metrics[milestone.metric] ?? 0) >= milestone.requiredValue : false;

    if (milestone && reached && !claimed.includes(id)) {
      writeJSON(key, [...claimed, id]);
      await tokenService.earn(milestone.tokenReward, 'milestone', `Milestone: ${milestone.name}`, REWARD_EXPIRY_DAYS);
    }
    return delay(this.getMilestones());
  },

  // --- Seasonal challenges -------------------------------------------------
  getChallenges(): Challenge[] {
    const state = readJSON<Record<string, { progress: number; joined: boolean }>>(CHALLENGE_KEY, {});
    return baseChallenges.map((c) => ({
      ...c,
      progress: state[c.id]?.progress ?? 0,
      joined: state[c.id]?.joined ?? false,
    }));
  },

  async joinChallenge(id: string): Promise<Challenge[]> {
    const state = readJSON<Record<string, { progress: number; joined: boolean }>>(CHALLENGE_KEY, {});
    state[id] = { progress: state[id]?.progress ?? 0, joined: true };
    writeJSON(CHALLENGE_KEY, state);
    return delay(this.getChallenges());
  },
};
